import { stdnum, type Validator } from "stdnum";

import type { AddressFieldNames } from "../../AddressForm/AddressForm.types";

import type { TaxDetailsFormValues } from "./TaxDetailsFormSection.types";

/** Ties the submit button to the form it submits. */
const TAX_DETAILS_FORM_ID = "tax-details-form";

/** Rates at or above this are rejected as a typo — a VAT rate never reaches it. */
const TAX_DETAILS_MAX_TAX_RATE = 35;

/** Where submit and cancel both return to. */
const TAX_DETAILS_FORM_RETURN_PATH = "/account/taxes";

/**
 * Decimal places kept when turning the stored fraction back into a percentage.
 * Enough for any real rate, and it stops 0.07 rendering as 7.000000000000001.
 */
const TAX_DETAILS_RATE_MAX_DECIMALS = 4;

/**
 * A validator for a registration `stdnum` holds no format for, checking the one
 * thing we know about it: how many characters it has once its separators are
 * stripped.
 */
const createTaxIdLengthValidator = ({
  abbreviation,
  length,
  name,
}: {
  abbreviation: string;
  length: number;
  name: string;
}): Validator => {
  const compact = (value: string) => value.replace(/[\s-]/g, "");

  return {
    abbreviation,
    compact,
    format: compact,
    localName: name,
    name,
    validate: (value) => {
      const compactValue = compact(value);

      // `stdnum` keeps its error classes internal; a plain `Error` has the same
      // shape as its `ValidationError`.
      return compactValue.length === length
        ? {
            compact: compactValue,
            isCompany: true,
            isIndividual: false,
            isValid: true,
          }
        : { error: new Error("Invalid length"), isValid: false };
    },
  };
};

/**
 * Each region's tax number, named explicitly: `stdnum`'s maps often pick wrong.
 * Keyed by country, or `${country}_${state}` for a province's own registration.
 * No entry means only presence is checked.
 */
const TAX_DETAILS_TAX_ID_VALIDATORS: Record<string, Validator> = {
  // Andorra
  AD: stdnum.AD.nrt,
  // Albania
  AL: stdnum.AL.nipt,
  // Angola
  AO: stdnum.AO.nif,
  // Argentina
  AR: stdnum.AR.cuit,
  // Austria
  AT: stdnum.AT.uid,
  // Australia
  AU: stdnum.AU.abn,
  // Azerbaijan
  AZ: stdnum.AZ.tin,
  // Belgium
  BE: stdnum.BE.vat,
  // Bulgaria
  BG: stdnum.BG.vat,
  // Brazil
  BR: stdnum.BR.cnpj,
  // Belarus
  BY: stdnum.BY.unp,
  // Belize
  BZ: stdnum.BZ.tin,
  // Canada
  CA: stdnum.CA.gst,
  // British Columbia, Canada
  CA_BC: stdnum.CA.pst,
  // Manitoba, Canada
  CA_MB: createTaxIdLengthValidator({
    abbreviation: "RST",
    length: 7,
    name: "Manitoba Retail Sales Tax Number",
  }),
  // Quebec, Canada
  CA_QC: stdnum.CA.qst,
  // Saskatchewan, Canada
  CA_SK: createTaxIdLengthValidator({
    abbreviation: "PST",
    length: 7,
    name: "Saskatchewan Provincial Sales Tax Number",
  }),
  // Switzerland
  CH: stdnum.CH.vat,
  // Chile
  CL: stdnum.CL.rut,
  // China
  CN: stdnum.CN.uscc,
  // Colombia
  CO: stdnum.CO.nit,
  // Costa Rica
  CR: stdnum.CR.cpj,
  // Cyprus
  CY: stdnum.CY.vat,
  // Czech Republic
  CZ: stdnum.CZ.dic,
  // Germany
  DE: stdnum.DE.vat,
  // Denmark
  DK: stdnum.DK.cvr,
  // Dominican Republic
  DO: stdnum.DO.rnc,
  // Algeria
  DZ: stdnum.DZ.nif,
  // Ecuador
  EC: stdnum.EC.ruc,
  // Estonia
  EE: stdnum.EE.kmkr,
  // Egypt
  EG: stdnum.EG.tn,
  // Spain
  ES: stdnum.ES.nif,
  // Finland
  FI: stdnum.FI.alv,
  // Faroe Islands
  FO: stdnum.FO.vn,
  // France
  FR: stdnum.FR.tva,
  // United Kingdom
  GB: stdnum.GB.vat,
  // Ghana
  GH: stdnum.GH.tin,
  // Guinea
  GN: stdnum.GN.nifp,
  // Greece
  GR: stdnum.GR.vat,
  // Guatemala
  GT: stdnum.GT.nit,
  // Croatia
  HR: stdnum.HR.oib,
  // Hungary
  HU: stdnum.HU.anum,
  // Indonesia
  ID: stdnum.ID.npwp,
  // Ireland
  IE: stdnum.IE.vat,
  // Israel
  IL: stdnum.IL.hp,
  // India
  IN: stdnum.IN.gstin,
  // Iceland
  IS: stdnum.IS.vsk,
  // Italy
  IT: stdnum.IT.iva,
  // Japan
  JP: stdnum.JP.cn,
  // Kenya
  KE: stdnum.KE.pin,
  // South Korea
  KR: stdnum.KR.brn,
  // Liechtenstein
  LI: stdnum.LI.peid,
  // Lithuania
  LT: stdnum.LT.pvm,
  // Luxembourg
  LU: stdnum.LU.tva,
  // Latvia
  LV: stdnum.LV.pvn,
  // Morocco
  MA: stdnum.MA.ice,
  // Monaco
  MC: stdnum.MC.tva,
  // Moldova
  MD: stdnum.MD.idno,
  // Montenegro
  ME: stdnum.ME.pib,
  // North Macedonia
  MK: stdnum.MK.edb,
  // Malta
  MT: stdnum.MT.vat,
  // Mexico
  MX: stdnum.MX.rfc,
  // Mozambique
  MZ: stdnum.MZ.nuit,
  // Netherlands
  NL: stdnum.NL.btw,
  // Norway
  NO: stdnum.NO.mva,
  // New Zealand
  NZ: stdnum.NZ.ird,
  // Peru
  PE: stdnum.PE.ruc,
  // Pakistan
  PK: stdnum.PK.ntn,
  // Poland
  PL: stdnum.PL.nip,
  // Portugal
  PT: stdnum.PT.nif,
  // Paraguay
  PY: stdnum.PY.ruc,
  // Romania
  RO: stdnum.RO.cif,
  // Serbia
  RS: stdnum.RS.pib,
  // Russia
  RU: stdnum.RU.inn,
  // Sweden
  SE: stdnum.SE.vat,
  // Singapore
  SG: stdnum.SG.uen,
  // Slovenia
  SI: stdnum.SI.ddv,
  // Slovakia
  SK: stdnum.SK.dph,
  // San Marino
  SM: stdnum.SM.coe,
  // El Salvador
  SV: stdnum.SV.nit,
  // Thailand
  TH: stdnum.TH.tin,
  // Tunisia
  TN: stdnum.TN.mf,
  // Turkey
  TR: stdnum.TR.vkn,
  // Taiwan
  TW: stdnum.TW.ubn,
  // Ukraine
  UA: stdnum.UA.edrpou,
  // Uruguay
  UY: stdnum.UY.rut,
  // Venezuela
  VE: stdnum.VE.rif,
  // Vietnam
  VN: stdnum.VN.mst,
  // South Africa
  ZA: stdnum.ZA.vat,
  // Bosnia and Herzegovina, Bolivia, Cuba, Hong Kong,
  // Mauritius, Malaysia, United States — no tax validation
};

/** The registered address, named as this form holds it. */
const TAX_DETAILS_ADDRESS_FIELDS = {
  city: "city",
  country: "country",
  postalCode: "zip",
  street: "street",
  unit: "unit",
  zone: "state",
} satisfies Record<keyof AddressFieldNames, keyof TaxDetailsFormValues>;

export {
  TAX_DETAILS_ADDRESS_FIELDS,
  TAX_DETAILS_FORM_ID,
  TAX_DETAILS_FORM_RETURN_PATH,
  TAX_DETAILS_MAX_TAX_RATE,
  TAX_DETAILS_RATE_MAX_DECIMALS,
  TAX_DETAILS_TAX_ID_VALIDATORS,
};
